import type { Role } from "@tanzeel/shared";
import {
  Suspense,
  createContext,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import type { AuthUser } from "../lib/auth";
import { Skeleton, ui } from "../ui";

/**
 * Application shell — role-aware navigation.
 *
 * Every screen is a lazy chunk. The clock-in path is what a supervisor opens
 * on a phone, so the initial download must not carry the dashboard, the
 * reconciliation queue or the setup CRUD (SPEC §12 perf budget).
 */

/**
 * Reload to pull the fresh build, but first let the newest service worker take
 * control — a plain reload is still served the *cached* stale index.html by the
 * old worker, so it would re-request the same missing chunk and fail again.
 * Prompting the waiting worker to activate and reloading on `controllerchange`
 * guarantees the reload gets the new index (with the new hashes). A 3s fallback
 * covers the case where there is no worker update to wait for.
 */
let reloadInFlight = false;
function scheduleFreshReload(): void {
  if (reloadInFlight) return; // one reload at a time, however many chunks fail
  reloadInFlight = true;
  const reload = (): void => window.location.reload();
  const sw = navigator.serviceWorker as ServiceWorkerContainer | undefined;
  if (sw === undefined) {
    reload();
    return;
  }
  sw.addEventListener("controllerchange", reload, { once: true });
  void sw
    .getRegistration()
    .then((registration) => {
      registration?.waiting?.postMessage({ type: "SKIP_WAITING" });
      return registration?.update();
    })
    .catch(() => {
      /* best-effort: the fallback timer still reloads */
    });
  // Short fallback: if no worker update fires (already-active new worker, or no
  // SW), reload promptly rather than leaving the tab on a spinner.
  window.setTimeout(reload, 1200);
}

/**
 * A dynamic import that fails is almost always a stale chunk right after a
 * deploy — the app shell references a hashed file the new deploy replaced.
 * Hold the pending screen and reload to the fresh build; a successful load
 * resets the counter, and we cap retries so a genuinely broken chunk can't
 * loop the tab forever.
 */
const RELOAD_KEY = "chunkReloads";
function withReload<M>(p: Promise<M>): Promise<M> {
  return p.then(
    (m) => {
      sessionStorage.removeItem(RELOAD_KEY);
      return m;
    },
    (err: unknown) => {
      const n = Number(sessionStorage.getItem(RELOAD_KEY) ?? "0");
      if (n < 3) {
        sessionStorage.setItem(RELOAD_KEY, String(n + 1));
        scheduleFreshReload();
        return new Promise<M>(() => {}); // hold until the reload navigates away
      }
      throw err;
    },
  );
}

const ClockInScreen = lazy(() =>
  withReload(import("./clock-in")).then((m) => ({ default: m.ClockInScreen })),
);
const ExpensesScreen = lazy(() =>
  withReload(import("./expenses")).then((m) => ({ default: m.ExpensesScreen })),
);
const RequestsScreen = lazy(() =>
  withReload(import("./requests")).then((m) => ({ default: m.RequestsScreen })),
);
const ManpowerScreen = lazy(() =>
  withReload(import("./labor-requests")).then((m) => ({
    default: m.LaborRequestsScreen,
  })),
);
const DashboardScreen = lazy(() =>
  withReload(import("./dashboard")).then((m) => ({ default: m.DashboardScreen })),
);
const ReconciliationScreen = lazy(() =>
  withReload(import("./reconciliation")).then((m) => ({ default: m.ReconciliationScreen })),
);
const SitesScreen = lazy(() =>
  withReload(import("./admin")).then((m) => ({ default: m.SitesScreen })),
);
const SettingsScreen = lazy(() =>
  withReload(import("./admin")).then((m) => ({ default: m.SettingsScreen })),
);
const NotificationsScreen = lazy(() =>
  withReload(import("./notifications")).then((m) => ({ default: m.NotificationsScreen })),
);
const PeopleScreen = lazy(() =>
  withReload(import("./people")).then((m) => ({ default: m.PeopleScreen })),
);
const ChangePasswordDialog = lazy(() =>
  withReload(import("./change-password")).then((m) => ({
    default: m.ChangePasswordDialog,
  })),
);

type Tab =
  | "clock-in"
  | "expenses"
  | "requests"
  | "manpower"
  | "dashboard"
  | "people"
  | "reconciliation"
  | "sites"
  | "settings"
  | "alerts";

interface TabDef {
  id: Tab;
  label: string;
  /** Optional shorter label for the mobile thumb bar, where width is tight. */
  short?: string;
  roles: readonly string[];
}

// Inlined (not imported from @tanzeel/shared) to keep the shared barrel out of
// the eager shell bundle — check-budget guards the pre-clock-in download path.
//
// The VALUES are copied, but `Record<Role, string>` is imported as a TYPE, which
// the build erases entirely: zero bundle cost, and adding a role to ROLES now
// fails to compile here until it is labelled. Adding GM meant editing three
// copies of this map with nothing to catch a miss; the copy stays for the
// budget, the silence does not.
const ROLE_LABELS: Record<Role, string> = {
  admin: "Admin",
  director: "Director",
  manager: "Manager",
  gm: "GM",
  supervisor: "Supervisor",
  site_engineer: "Site engineer",
  driver: "Driver",
};

const TABS: readonly TabDef[] = [
  { id: "clock-in", label: "Clock in", roles: ["supervisor", "site_engineer", "driver", "gm"] },
  { id: "dashboard", label: "Dashboard", short: "Home", roles: ["manager", "director", "admin"] },
  { id: "people", label: "People", roles: ["manager", "director", "admin"] },
  { id: "sites", label: "Sites", roles: ["manager", "director", "admin"] },
  { id: "expenses", label: "Receipts", roles: ["supervisor", "site_engineer", "driver", "gm"] },
  { id: "requests", label: "Requests", short: "Req", roles: ["supervisor", "site_engineer", "driver", "gm", "manager", "director", "admin"] },
  // No "gm": a GM clocks in and submits receipts, but does not staff a site.
  // The id stays "manpower" — it is in saved history entries and the
  // dashboard deep-links — but the screen is called Crew everywhere it is
  // read. Desktop said "Manpower" and the phone said "Crew", so two people
  // could not name the same screen the same way.
  { id: "manpower", label: "Crew", roles: ["supervisor", "site_engineer", "driver", "manager", "director", "admin"] },
  { id: "reconciliation", label: "Finance", roles: ["manager", "director", "admin"] },
  // "settings" and "alerts" are intentionally not tabs — they live in the
  // header ("Settings" word for admin, and the alerts bell).
];

/**
 * In-app navigation is backed by the browser History API so the phone's Back
 * button steps through tabs (and a screen's sub-views, e.g. a person profile)
 * instead of leaving the app on the first press. A location is a tab plus an
 * optional opaque `sub` id owned by the active screen.
 */
interface NavLocation {
  tab: Tab;
  sub: string | null;
}

interface NavApi {
  /** The active sub-view id for the current tab, or null at the tab root. */
  sub: string | null;
  /** Open a sub-view (pushes a history entry so Back closes it). */
  pushSub: (id: string) => void;
  /** Swap the current sub-view in place (no new entry; Back still exits it). */
  replaceSub: (id: string) => void;
  /** Close the current sub-view (equivalent to pressing Back). */
  back: () => void;
}

const NavContext = createContext<NavApi | null>(null);

/** Screens use this to register a sub-view with the browser back stack. */
export function useNav(): NavApi {
  const value = useContext(NavContext);
  if (value === null) throw new Error("useNav used outside AppShell");
  return value;
}

export function AppShell({
  user,
  siteIds,
  onSignOut,
}: {
  user: AuthUser;
  siteIds: string[] | null;
  onSignOut: () => void;
}): ReactNode {
  const tabs = TABS.filter((t) => t.roles.includes(user.role));
  const homeTab = tabs[0]?.id ?? "alerts";
  const [loc, setLoc] = useState<NavLocation>({ tab: homeTab, sub: null });
  const tab = loc.tab;

  // Seed the base history entry so the home tab is the app's root: Back from
  // there exits, Back from anywhere else returns here. Merge into the existing
  // state so the router's own history bookkeeping is preserved.
  useEffect(() => {
    window.history.replaceState(
      { ...window.history.state, nav: { tab: homeTab, sub: null } },
      "",
    );
  }, [homeTab]);

  // Back/forward replays whatever location that entry recorded.
  useEffect(() => {
    const onPop = (e: PopStateEvent): void => {
      const next = (e.state as { nav?: NavLocation } | null)?.nav;
      setLoc(next ?? { tab: homeTab, sub: null });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [homeTab]);

  // Keep the installed app fresh. When a newly deployed service worker takes
  // control, reload ONCE so the tab isn't left pointing at hashed chunks the
  // deploy replaced (the "Failed to fetch dynamically imported module" crash).
  // Guards: never on first install (no prior controller), never twice (flag) —
  // so it can't loop. Also nudge an update check when the tab regains focus, so
  // a deploy is noticed promptly instead of only when a chunk fails to load.
  useEffect(() => {
    const sw = navigator.serviceWorker as ServiceWorkerContainer | undefined;
    if (sw === undefined) return;
    const hadController = sw.controller !== null;
    let reloading = false;
    const onControllerChange = (): void => {
      if (reloading || !hadController) return;
      reloading = true;
      window.location.reload();
    };
    const checkForUpdate = (): void => {
      void sw
        .getRegistration()
        .then((r) => r?.update())
        .catch(() => {
          /* best-effort */
        });
    };
    const onVisible = (): void => {
      if (document.visibilityState === "visible") checkForUpdate();
    };
    sw.addEventListener("controllerchange", onControllerChange);
    document.addEventListener("visibilitychange", onVisible);
    checkForUpdate();
    return () => {
      sw.removeEventListener("controllerchange", onControllerChange);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const go = useCallback((next: NavLocation, replace = false): void => {
    setLoc((current) => {
      if (current.tab === next.tab && current.sub === next.sub) return current;
      const entry = { ...window.history.state, nav: next };
      if (replace) window.history.replaceState(entry, "");
      else window.history.pushState(entry, "");
      return next;
    });
  }, []);

  // Tapping the header brand returns to the role's first tab (home).
  useEffect(() => {
    const goHome = (): void => go({ tab: homeTab, sub: null });
    window.addEventListener("tanzeel:home", goHome);
    return () => window.removeEventListener("tanzeel:home", goHome);
  }, [homeTab, go]);

  // Screens (e.g. dashboard cards) can jump to another tab by dispatching this,
  // but only to a tab this role is actually allowed to see.
  useEffect(() => {
    const onNav = (e: Event): void => {
      // detail is either a tab id, or { tab, sub } to deep-link into a hub
      // screen's sub-view (e.g. the Requests tab's Overtime/Materials segment).
      const detail = (e as CustomEvent<string | { tab: string; sub?: string }>)
        .detail;
      const target = typeof detail === "string" ? detail : detail.tab;
      const sub = typeof detail === "string" ? null : detail.sub ?? null;
      if (TABS.some((t) => t.id === target && t.roles.includes(user.role))) {
        go({ tab: target as Tab, sub });
      }
    };
    window.addEventListener("tanzeel:nav", onNav);
    return () => window.removeEventListener("tanzeel:nav", onNav);
  }, [user.role, go]);

  // The alerts bell lives in the brand header (above the shell) and opens the
  // alerts screen through this event.
  useEffect(() => {
    const openAlerts = (): void => go({ tab: "alerts", sub: null });
    window.addEventListener("tanzeel:alerts", openAlerts);
    return () => window.removeEventListener("tanzeel:alerts", openAlerts);
  }, [go]);

  // The header "Settings" word (admin only) opens the settings screen.
  useEffect(() => {
    const openSettings = (): void => {
      if (user.role === "admin") go({ tab: "settings", sub: null });
    };
    window.addEventListener("tanzeel:settings", openSettings);
    return () => window.removeEventListener("tanzeel:settings", openSettings);
  }, [user.role, go]);

  const navApi = useMemo<NavApi>(
    () => ({
      sub: loc.sub,
      pushSub: (id: string) => go({ tab: loc.tab, sub: id }),
      replaceSub: (id: string) => go({ tab: loc.tab, sub: id }, true),
      back: () => window.history.back(),
    }),
    [loc.sub, loc.tab, go],
  );

  const setTab = (id: Tab): void => go({ tab: id, sub: null });
  const [changingPassword, setChangingPassword] = useState(false);

  // Change password + sign out live in the brand header (above the shell) and
  // reach their handlers — which own the dialog and the session — through these
  // events, the same pattern as the alerts bell.
  useEffect(() => {
    const openChangePw = (): void => setChangingPassword(true);
    window.addEventListener("tanzeel:changepw", openChangePw);
    window.addEventListener("tanzeel:signout", onSignOut);
    return () => {
      window.removeEventListener("tanzeel:changepw", openChangePw);
      window.removeEventListener("tanzeel:signout", onSignOut);
    };
  }, [onSignOut]);

  return (
    <div className="space-y-4 pb-thumb sm:pb-0">
      <div className="flex items-center gap-3 text-xs text-muted">
        <span className="min-w-0 truncate">
          <span className="font-semibold text-ink">{user.name}</span> ·{" "}
          {ROLE_LABELS[user.role as Role] ?? user.role}
        </span>
      </div>

      {changingPassword ? (
        <Suspense fallback={null}>
          <ChangePasswordDialog onClose={() => setChangingPassword(false)} />
        </Suspense>
      ) : null}

      {/*
        Two navs, one source of truth. On a phone the top of the screen cannot
        be reached with the hand holding it, so supervisors get a fixed thumb
        bar; on a wider screen (management, admin) the tabs sit inline where a
        pointer expects them.
      */}
      <nav
        aria-label="Sections"
        className="-mx-4 hidden gap-1 overflow-x-auto px-4 pb-1 sm:flex"
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            aria-current={tab === t.id ? "page" : undefined}
            className={`${ui.btn} shrink-0 px-3.5 py-2 text-sm ${
              tab === t.id
                ? "bg-ink text-white"
                : "text-muted hover:bg-sunk hover:text-ink"
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <nav
        aria-label="Sections"
        className="fixed inset-x-0 bottom-0 z-30 flex border-t border-line bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur sm:hidden"
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            aria-current={tab === t.id ? "page" : undefined}
            className={`relative flex min-h-[56px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-0.5 text-[11px] font-medium ${
              tab === t.id ? "text-ink" : "text-faint"
            }`}
          >
            {tab === t.id ? (
              <span aria-hidden="true" className="absolute inset-x-2 top-0 h-0.5 rounded-full bg-hiviz" />
            ) : null}
            <span className="max-w-full truncate leading-tight">{t.short ?? t.label}</span>
          </button>
        ))}
      </nav>

      <NavContext.Provider value={navApi}>
        <Suspense fallback={<Skeleton rows={4} />}>
          {tab === "clock-in" ? <ClockInScreen /> : null}
          {tab === "expenses" ? <ExpensesScreen siteIds={siteIds} /> : null}
          {tab === "requests" ? (
            <RequestsScreen role={user.role} siteIds={siteIds} />
          ) : null}
          {tab === "manpower" ? (
            <ManpowerScreen role={user.role} siteIds={siteIds} />
          ) : null}
          {tab === "dashboard" ? <DashboardScreen role={user.role} /> : null}
          {tab === "people" ? <PeopleScreen role={user.role} /> : null}
          {tab === "reconciliation" ? <ReconciliationScreen role={user.role} /> : null}
          {tab === "sites" ? <SitesScreen role={user.role} /> : null}
          {tab === "settings" ? <SettingsScreen role={user.role} /> : null}
          {tab === "alerts" ? <NotificationsScreen /> : null}
        </Suspense>
      </NavContext.Provider>
    </div>
  );
}
